/**
 * ArWorkflowMenu Component
 *
 * Dropdown menu for triggering workflows on selected rows.
 * Shows available workflows based on table and form context.
 * When no rows are selected, assumes all rows are included.
 *
 * Form data comes from `useFormByParentPath(parentPath)` — the table's own
 * parent, which is not necessarily the routed form. The only form-store leaf it
 * reads is `splitQuery`, taken off a narrow snapshot: `useFormStore()` is banned
 * here because its spread of `formVersion.data` re-rendered this menu on every
 * keystroke in the surrounding form (SYD-2252).
 *
 * It holds no subscription to the version document at all — the sidebar resolves
 * its own source from `sourceParentPath`, behind `destroyOnHidden` (SYD-2264).
 *
 * Every run sends a `rowScope` from `useArTableWorkflowRowScope`, which reads the
 * rows imperatively at click time under the same ban. It is handed to
 * `useWorkflowRunner` rather than awaited here, so `isRunning` — which disables both
 * the trigger and the menu item — covers the whole-table fetch it may perform; a
 * refusal comes back as a `null` result and abandons the run (SYD-2322).
 *
 * `runWorkflowFromMenu` reports its outcome back to the sidebar, which latches its
 * own Run button on it; the toasts here are unchanged and run in parallel. The
 * sidebar must be closed before navigating to a newly created form, or the form
 * renders behind the mask.
 *
 * A successful run leaves its selection clear pending, and whoever closes the run's
 * UI performs it: the sidebar's Cancel or Done, the navigation away, or — on the
 * parameterless path, which opens no sidebar — the click handler itself. Clearing
 * while the sidebar is open would flip its row count out from under the banner
 * describing the run, and a second Run after unlatching Done would read an empty
 * selection and cover the whole table (SYD-2442).
 */

import { FC, memo, useRef, useState, useContext } from 'react';
import { Button, Dropdown } from 'antd';
import Icon from '../../../components/atoms/Icon';
import type { MenuProps } from 'antd';
import { useSnapshot } from 'valtio';
import { ArTableUIStateContext } from '../context';
import { formStore } from '@/store';
import { MessageContext } from '@/providers/MessageContext';
import type { ArTableId } from '#types/System.Artifacts.types';
import type { SplitQuery, UIArTableColumnOverride } from '#types/System.types';
import type { Workflow, WorkflowRunAttachments } from '#types';
import type { WorkflowRunOutcome } from '@/workflows/runtime/workflowRunOutcome.types';
import { useApplicableWorkflows } from '@/use/useApplicableWorkflows';
import WorkflowParameterSidebar from '@/workflows/runtime/WorkflowParameterSidebar';
import { useNavigate } from 'react-router-dom';
import { useFormByParentPath } from '@/workflows/runtime/useFormByParentPath';
import { clearSelection, getSelectedRowIds, type ArTableSelectionState } from '@/workflows/runtime/arTableSelection';
import { workflowNavigatesToNewForm, workflowNeedsRuntimeParameters } from '@/workflows/runtime/workflowPredicates';
import { useWorkflowRunner } from '@/workflows/runtime/useWorkflowRunner';
import { useArTableExportActions } from '../export';
import { useArTableWorkflowRowScope } from '../visibleRows';
import formatSignifySuccessMessage from './formatSignifySuccessMessage';
import { workflowRunErrorOutcome } from '@/workflows/runtime/workflowRunErrorOutcome';
import { workflowRunStoppedOutcome } from '@/workflows/runtime/workflowRunStoppedOutcome';

type ArWorkflowMenuProps = {
  tableId: ArTableId;
  parentPath: string;
  disabled?: boolean;
  columnConfig?: UIArTableColumnOverride[];
};

const ArWorkflowMenu: FC<ArWorkflowMenuProps> = ({
  tableId,
  parentPath,
  disabled = false,
  columnConfig,
}) => {
  const navigate = useNavigate();
  const { messageApi, handleFirestoreWriteError } = useContext(MessageContext);
  const uiState = useContext(ArTableUIStateContext)!;
  const splitQuery = useSnapshot(formStore.state).splitQuery as SplitQuery | undefined;
  const { formId, versionId, formType, formNumber } = useFormByParentPath(parentPath);
  const selectionSnap = useSnapshot(uiState.selection);
  const paginationSnap = useSnapshot(uiState.pagination);

  const [workflowModal, setWorkflowModal] = useState<Workflow | null>(null);
  /** Set by a successful run, consumed by whoever closes that run's UI. */
  const selectionClearPending = useRef(false);

  const selection = uiState.selection;
  const selectedCount = selectionSnap.selectedCount;
  const isAllRows = selectedCount === 0;
  const tableSelection = selection as ArTableSelectionState;

  const workflowQueryEnabled = !!formId && !!versionId;
  const { workflows, loading } = useApplicableWorkflows({
    triggerType: 'bulkRowAction',
    formType,
    tableId,
    enabled: workflowQueryEnabled,
  });

  const { exportMenuItems, exporting } = useArTableExportActions({ tableId, parentPath, disabled, columnConfig });

  const { resolveRowScope } = useArTableWorkflowRowScope({ tableId, parentPath });
  const { isRunning, runWorkflow, stopRun, stopPhase } = useWorkflowRunner({
    sourceFormId: formId,
    sourceVersionId: versionId,
    sourceTableId: tableId,
    resolveRowScope,
  });

  const clearSelectionAfterRun = () => {
    if (!selectionClearPending.current) return;
    selectionClearPending.current = false;
    clearSelection(tableSelection);
  };

  const runWorkflowFromMenu = async (
    workflow: Workflow,
    params: Record<string, unknown>,
    attachments?: WorkflowRunAttachments
  ): Promise<WorkflowRunOutcome | null> => {
    try {
      const result = await runWorkflow({
        workflow,
        userParameters: params,
        selectedRowIds: getSelectedRowIds(tableSelection),
        attachments,
      });
      if (!result) return null;
      if (!result.success) {
        const stopped = workflowRunStoppedOutcome(result, workflow);
        messageApi.warning(stopped.text, 10);
        return stopped;
      }

      const apiResult = result.actions
        ?.findLast((a) => a.actionType === 'callSignifyApi')
        ?.result as Record<string, unknown> | undefined;
      const successMessage = formatSignifySuccessMessage(apiResult, workflow.description?.shortLabel);
      messageApi.success(successMessage, 10);

      if (typeof apiResult?.warning === 'string' && apiResult.warning.length > 0) {
        messageApi.warning(apiResult.warning, 15);
      }

      // Surface engine-side no-op reasons inline (e.g. selectedRows scope received zero rows).
      const noOpReasons = (result.actions ?? [])
        .filter((a) => a.actionType === 'updateCells')
        .map((a) => (a.result as { reason?: string } | undefined)?.reason)
        .filter((r): r is string => typeof r === 'string' && r.length > 0);
      for (const reason of noOpReasons) {
        messageApi.warning(reason, 10);
      }

      selectionClearPending.current = true;

      if (result.newFormId && result.newVersionId && workflowNavigatesToNewForm(workflow)) {
        setWorkflowModal(null);
        clearSelectionAfterRun();
        navigate(`/forms/${result.newFormId}/formVersions/${result.newVersionId}`);
      }

      return {
        status: 'success',
        text: successMessage,
        summary: result.summaries?.find((s) => s.actionType === 'send'),
      };
    } catch (error) {
      const outcome = workflowRunErrorOutcome(error, workflow.description?.shortLabel);
      handleFirestoreWriteError(error, '[ArWorkflowMenu] execute workflow', { userMessage: outcome.text });
      return outcome;
    }
  };

  const handleWorkflowClick = async (workflow: Workflow) => {
    if (!workflow.docId) return;
    if (workflowNeedsRuntimeParameters(workflow)) {
      setWorkflowModal(workflow);
      return;
    }
    await runWorkflowFromMenu(workflow, {});
    clearSelectionAfterRun();
  };

  const rowCountLabel = isAllRows
    ? (splitQuery?.queryValue ? `Rows split by ${splitQuery.queryValue}` : 'All rows')
    : `${selectedCount} row${selectedCount === 1 ? '' : 's'}`;

  const items: MenuProps['items'] = [
    {
      key: 'row-count',
      label: rowCountLabel,
      disabled: true,
      style: { opacity: 0.7 },
    },
    { type: 'divider' },
    ...exportMenuItems ?? [],
    { type: 'divider' },
    ...workflows.map((w) => {
      const exceedsMax = w.maxSelectedRows != null && w.maxSelectedRows > 0 && (isAllRows || selectedCount > w.maxSelectedRows);
      const baseLabel = w.description?.shortLabel || w.docId;
      return {
        key: `workflow:${w.docId}`,
        label: exceedsMax ? `${baseLabel} (select ${w.maxSelectedRows} row${w.maxSelectedRows === 1 ? '' : 's'})` : baseLabel,
        onClick: () => {
          void handleWorkflowClick(w);
        },
        disabled: disabled || isRunning || exceedsMax,
      };
    }),
  ];

  return (
    <>
      <Dropdown
        menu={{ items }}
        trigger={['click']}
        disabled={disabled || isRunning || exporting}
      >
        <Button
          aria-label="artable-workflow-menu-button"
          type="link"
          size="small"
          loading={isRunning || loading || exporting}
          icon={<Icon.ChevronsDown />}
          disabled={disabled || isRunning || exporting}
        >
          Workflow
        </Button>
      </Dropdown>

      <WorkflowParameterSidebar
        workflow={workflowModal}
        open={workflowModal !== null}
        sourceParentPath={parentPath}
        sourceFormId={formId}
        sourceVersionId={versionId}
        tableScope={{
          selectedCount,
          totalCount: paginationSnap.totalRows,
          selectedRowIds: getSelectedRowIds(tableSelection),
          splitLabel: splitQuery?.queryValue,
          splitFilter: splitQuery
            ? { columnId: splitQuery.splitRef.valuePath, value: splitQuery.queryValue }
            : undefined,
        }}
        onCancel={() => {
          setWorkflowModal(null);
          clearSelectionAfterRun();
        }}
        onConfirm={async (params, attachments) => {
          const w = workflowModal;
          if (!w) return null;
          return runWorkflowFromMenu(w, params, attachments);
        }}
        onStop={stopRun}
        stopPhase={stopPhase}
        formNumber={formNumber}
        formTitle={formNumber ?? 'Form'}
      />
    </>
  );
};

export default memo(ArWorkflowMenu);
